/**
 * MultiVendorShowcase, Sprint 2.5a A.0/B/S6b.
 *
 * Replaces the slot the parked DessertShowcase used to occupy
 * (between hero and HowLembraWorks). The DessertShowcase was 100%
 * cake-and-pastry from two bakeries (Le Yeahllow + Chéri); after
 * the new hero promises a multi-vendor concierge, that re-narrowed
 * the brand to "cakes only" within one scroll.
 *
 * This component fills the same visual slot but signals the actual
 * catalogue breadth (cakes, hampers, flowers, vouchers, custom) by
 * interleaving two card types in a two-row marquee:
 *
 *   1. **Product cards** (real photos): the existing Le Yeahllow +
 *      Chéri Shopify CDN cake/pastry photos. These provide the
 *      photographic dopamine the original DessertShowcase had.
 *
 *   2. **Category cards** (typographic + gradient swatch): one card
 *      per non-cake category. No fake product photos, no invented
 *      vendor names, just the category name in Newsreader italic
 *      with a small descriptor of what's actually inside. Honest
 *      placeholders until we have real product photography.
 *
 * Open follow-up Lucky raised: source product photos from vendor
 * Instagram accounts. When those photos land, the category cards
 * for hampers/flowers/vouchers get swapped for product cards. The
 * data shape (`CarouselItem`) supports both kinds today via the
 * `kind` discriminant, adding photos is a one-paste change.
 *
 * Pattern lifted from the parked DessertShowcase (marquee mechanism,
 * pause-on-hover, prefers-reduced-motion respect). Visual treatment
 * shifted to a butter→cream gradient so the section bridges from
 * the hero butter into the cream beat below, instead of breaking
 * to navy (HowLembraWorks already carries the navy beat).
 */

import { useState } from "react";
import { Link } from "react-router-dom";
// MS24: only ArrowRight needed now. The Custom-briefs gradient
// placeholder (last `kind: "category"` card) was dropped in this
// sprint, so neither Sparkles nor Package are referenced anywhere.
// The CarouselItem type discriminant is kept for future flexibility,
// but `typeof Package` is replaced with a structural alias so the
// import isn't required.
import { ArrowRight } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { AnimatedReveal } from "@/components/hero/shared/AnimatedReveal";

const monoFamily =
  "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace";
const displayFamily = "Newsreader, Georgia, serif";
const sansFamily = "DM Sans, sans-serif";

/* ─── Card data shape ────────────────────────────────────────────
   Discriminated union, `kind: "product"` when we have a real
   photo, `kind: "category"` for the placeholder cards we'll
   eventually replace with real vendor product photos. */
type CarouselItem =
  | {
      kind: "product";
      name: string;
      vendor: string;
      categoryTag: string;
      image: string;
      alt: string;
    }
  | {
      kind: "category";
      name: string;
      descriptor: string;
      categoryTag: string;
      /** Tailwind/CSS gradient for the swatch background. */
      swatch: string;
      /** Lucide icon component shown over the swatch. */
      Icon: LucideIcon;
      /** Where clicking the card sends the user. */
      href: string;
    };

/* ─── Carousel cards (MS13 rebalance) ──────────────────────────
   Lucky's MS12 critique: too many desserts, top + bottom rows show
   the same photos. New balance:

     ROW A: 2 cakes + 6 non-cake products (hampers, flowers, vouchers)
     ROW B: 2 cakes + 5 non-cake products + 1 custom-brief placeholder

   Total unique products surfaced: 4 cakes + 11 non-cakes + 1 placeholder.
   ROWS ARE FULLY DISJOINT, no card appears in both rows. The
   `interleave()` fn below builds them from these arrays.

   All photos live on each vendor's public CDN (Shopify, WordPress,
   HubSpot). Sourced + verified during MS12; sourcing notes per
   vendor live in the Notion Vendor Image Sourcing database
   (collection://427e9f0b-21a5-461d-bacc-f449f132c947). */

// Cakes, 4 unique, picked for visual variety (golden, colorful,
// red, yellow). Was 7 in MS12; trimmed per Lucky's "too many
// desserts" call.
const CAKE_PRODUCTS: CarouselItem[] = [
  {
    kind: "product",
    name: "Yuzu Hachimitsu",
    vendor: "LE YEAHLLOW",
    categoryTag: "Cake",
    image: "https://cdn.shopify.com/s/files/1/0596/6636/6661/files/IMG-3665.png",
    alt: "Yuzu Hachimitsu layered honey cake from Le Yeahllow",
  },
  {
    kind: "product",
    name: "Eye Candy Grand Gâteau",
    vendor: "CHÉRI",
    categoryTag: "Cake",
    image: "https://cdn.shopify.com/s/files/1/0720/0122/4916/files/EYECANDY1.png",
    alt: "Eye Candy Grand Gâteau French patisserie cake from Chéri",
  },
  {
    kind: "product",
    name: "Strawberry mille feuille",
    vendor: "LE YEAHLLOW",
    categoryTag: "Cake",
    image: "https://cdn.shopify.com/s/files/1/0596/6636/6661/files/D0F6EAC2-6550-40CF-897E-BE0B2B181710.png",
    alt: "Strawberry mille feuille from Le Yeahllow",
  },
  {
    kind: "product",
    name: "Lemon meringue tart",
    vendor: "CHÉRI",
    categoryTag: "Cake",
    image: "https://cdn.shopify.com/s/files/1/0720/0122/4916/files/LEMMERINGUE1.png",
    alt: "Whole lemon meringue tart from Chéri",
  },
];

// Non-cake products for ROW A (6 cards). All disjoint from row B.
const ROW_A_NON_CAKE: CarouselItem[] = [
  {
    kind: "product",
    name: "Premium Local Showcase",
    vendor: "CREMORNE STREET HAMPERS",
    categoryTag: "Hamper",
    image:
      "https://cremornestreethampers.com.au/cdn/shop/files/GOURMET_HAMPER_25_AC_8654.jpg?v=1758244403",
    alt: "Premium local showcase hamper from Cremorne Street Hampers",
  },
  {
    kind: "product",
    name: "Garden Posy",
    vendor: "VICTORIA WHITELAW",
    categoryTag: "Flowers",
    image:
      "https://www.victoriawhitelaw.com.au/cdn/shop/files/Sweetheart.jpg?v=1749536688&width=720",
    alt: "Garden posy bouquet from Victoria Whitelaw, Melbourne",
  },
  {
    kind: "product",
    name: "Paint & Sip Voucher",
    vendor: "PINOT & PICASSO",
    categoryTag: "Voucher",
    // MS25: swapped from the gift-card-package art to Lucky's
    // curated Notion photo of an actual paint-and-sip session.
    // Reads as "what the experience looks like," not "what the
    // gift card packaging looks like."
    image: "/vendor-photos/pinot-picasso/sip_paint.jpg",
    alt: "Paint and sip session at Pinot & Picasso, Melbourne",
  },
  {
    kind: "product",
    name: "Bathing Voucher",
    vendor: "MORNINGTON PENINSULA HOT SPRINGS",
    categoryTag: "Voucher",
    // MS25: swapped from HubSpot CDN to Lucky's curated Notion
    // photo of the springs themselves.
    image: "/vendor-photos/peninsula-hot-springs/spa1.jpg",
    alt: "Mornington Peninsula Hot Springs bathing voucher",
  },
  {
    kind: "product",
    name: "Mineral Bath Voucher",
    vendor: "HEPBURN BATHHOUSE",
    categoryTag: "Voucher",
    image:
      "https://hepburnbathhouse.com/wp-content/uploads/sites/11/2024/09/HepburnSprings-2024-9895-1-1-1024x683.jpg",
    alt: "Mineral bath voucher at Hepburn Bathhouse, regional Victoria",
  },
  {
    kind: "product",
    name: "Cooking Class",
    vendor: "OTAO KITCHEN",
    categoryTag: "Voucher",
    image: "https://otaokitchen.com.au/uploads/OTAOClasses82.jpg",
    alt: "Vietnamese cooking class voucher from Otao Kitchen, Docklands",
  },
];

// MS25 — Lucky's curated Notion photos NOW SERVED FROM
// /public/vendor-photos/. Pulled via the Notion REST API once the
// Vendor Image Sourcing database was shared with the Lembra
// Prospecting Agent integration. Each photo is a stable static
// asset (no CDN dependency, no expiring signed URLs).
//
// All 11 vendors he uploaded for are now featured in MVS. New
// additions vs MS24: Inner Studio (Sanity-CDN was a poor fallback;
// his curated Inner Studio shots are MUCH better), Larkspur,
// Cecilia Fox, Proud Mary's. Also swapped: Pinot Picasso (gift-card
// art → real paint-sip session photo), Mornington Peninsula
// (HubSpot CDN → Lucky's spa shots), Maker & Monger (Squarespace
// CDN → his hampers shots), ST. ALi (coffee bag → his roastery
// shot), Patricia (their wordmark → his curated version),
// Botanique Workshop (Shopify CDN → his studio shots).
//
// MS46b rename: Aunty Peg's → Proud Mary's. Proud Mary's is the
// parent roastery; Aunty Peg's is one of their cafes. We were
// using a sub-brand name; the parent name is the canonical one.
// Same beans, same vibes, more accurate attribution.
const ASPIRATIONAL_PRODUCTS: CarouselItem[] = [
  {
    kind: "product",
    name: "Cheese hamper",
    vendor: "MAKER & MONGER",
    categoryTag: "Hamper",
    image: "/vendor-photos/maker-and-monger/hampers1.webp",
    alt: "Cheese and charcuterie hamper from Maker & Monger, Prahran",
  },
  {
    kind: "product",
    name: "Coffee voucher",
    vendor: "PATRICIA COFFEE",
    categoryTag: "Voucher",
    // MS46: Lucky uploaded a stronger picnic-trio shot (moka pot +
    // 3 Patricia tins on gingham cloth + small daisy bouquet in a
    // white vase). Replaces the previous wordmark-style Patricia.avif.
    image: "/vendor-photos/patricia-coffee/picnic-trio.jpg",
    alt: "Patricia Coffee Brewers gift voucher, Melbourne CBD",
  },
  {
    kind: "product",
    name: "Coffee voucher",
    vendor: "ST. ALi",
    categoryTag: "Voucher",
    image: "/vendor-photos/stali/coffee7.webp",
    alt: "ST. ALi gift voucher, South Melbourne",
  },
  // MS282: DEPRECATED — Botanique Workshop never made it into the
  // DB (per Lucky's own Notion note: "this isnt a florist shop for
  // small gift giving, they do events"). Entry kept in the array
  // for index stability + so anyone re-onboarding them later has
  // the image/copy reference; the interleave() row-build skips
  // this slot. Don't reference [3] in rowA/rowB.
  {
    kind: "product",
    name: "Studio bouquet (DEPRECATED — not in catalogue)",
    vendor: "BOTANIQUE WORKSHOP",
    categoryTag: "Flowers",
    image: "/vendor-photos/botanique-workshop/flowers12.jpg",
    alt: "Studio bouquet from Botanique Workshop",
  },
  {
    kind: "product",
    name: "Inner Studio class",
    vendor: "INNER STUDIO",
    categoryTag: "Voucher",
    image: "/vendor-photos/inner-studio/innerstudio1.jpg",
    alt: "Inner Studio class voucher, Melbourne",
  },
  {
    kind: "product",
    name: "Hand-tied bouquet",
    vendor: "LARKSPUR",
    categoryTag: "Flowers",
    image: "/vendor-photos/larkspur/flowers1.jpg",
    // MS282: was "Larkspur, Hawthorn" — the actual Larkspur Flowers
    // business is in Daylesford, not Hawthorn (Notion's location
    // field was wrong; corrected during MS281 vendor onboarding).
    alt: "Hand-tied bouquet from Larkspur, Daylesford",
  },
  // MS282: DEPRECATED — Cecilia Fox closed her Melbourne studio
  // and relocated to Auckland NZ (per ceciliafox.com). Adding a
  // Melbourne row would fabricate an operation that no longer
  // exists, so no DB entry. Entry kept in the array for index
  // stability; the interleave() row-build skips this slot. Don't
  // reference [6] in rowA/rowB.
  {
    kind: "product",
    name: "Wild bouquet (DEPRECATED — vendor relocated to Auckland NZ)",
    vendor: "CECILIA FOX",
    categoryTag: "Flowers",
    image: "/vendor-photos/cecilia-fox/flowers_5.jpg",
    alt: "Unstructured bouquet from Cecilia Fox, Northcote",
  },
  {
    kind: "product",
    name: "Coffee gift",
    vendor: "PROUD MARY'S",
    categoryTag: "Voucher",
    image: "/vendor-photos/proud-marys/coffee3.jpg",
    alt: "Coffee gift voucher from Proud Mary's, Collingwood",
  },
];

// Non-cake products for ROW B (5 cards). All disjoint from row A
//, different products from the SAME vendors are explicitly
// allowed (Cremorne, Victoria Whitelaw, Pinot, Peninsula all
// recur with different SKUs).
const ROW_B_NON_CAKE: CarouselItem[] = [
  {
    kind: "product",
    name: "Champagne and Luxury",
    vendor: "CREMORNE STREET HAMPERS",
    categoryTag: "Hamper",
    image:
      "https://cremornestreethampers.com.au/cdn/shop/files/HMP_TASTE_OF_LUXURY_AC_8542.jpg?v=1727067744",
    alt: "Champagne and luxury hamper from Cremorne Street Hampers",
  },
  {
    kind: "product",
    name: "White Delight",
    vendor: "VICTORIA WHITELAW",
    categoryTag: "Flowers",
    image:
      "https://www.victoriawhitelaw.com.au/cdn/shop/files/White-Delight-flower-arrangement-melbourne.jpg?v=1749534237&width=720",
    alt: "White Delight flower arrangement from Victoria Whitelaw",
  },
  {
    kind: "product",
    name: "Couples Class Voucher",
    vendor: "PINOT & PICASSO",
    categoryTag: "Voucher",
    // MS25: swapped to Lucky's curated Notion shot of the actual
    // paint-and-sip studio environment (was the gift-card art).
    image: "/vendor-photos/pinot-picasso/sip_paint2.webp",
    alt: "Pinot & Picasso paint-and-sip studio voucher",
  },
  {
    kind: "product",
    name: "Spa Voucher",
    vendor: "ENDOTA SPA",
    categoryTag: "Voucher",
    image:
      "https://endotaspa.com.au/cdn/shop/files/organic_relax.jpg?v=1754969028&width=720",
    alt: "Organic relax spa voucher from Endota Spa",
  },
  {
    kind: "product",
    name: "Veuve Clicquot Celebration",
    vendor: "THE HAMPER BOUTIQUE",
    categoryTag: "Hamper",
    image:
      "https://www.thehamperboutique.com.au/cdn/shop/files/veuve-clicquot-premium-celebration-hamper-391259.jpg?v=1762038023&width=1200",
    alt: "Veuve Clicquot premium celebration hamper from The Hamper Boutique",
  },
];

/* MS24: Custom-brief gradient placeholder REMOVED. Lucky's call —
   the butter gradient + sparkle icon read as a confused stand-in
   ("very stupid, doesn't make sense"). Replaced by a real spa card
   in row B so every card in the marquee is a real product photo.
   The "custom briefs" route is still surfaced via the SmartIntentBar
   in the hero + the FinalCTA at the bottom of the page; it doesn't
   need a card slot. */

/* ─── Build interleaved rows (MS13 rev) ────────────────────────
   Strict no-overlap rule: every card in rowA is different from every
   card in rowB (different vendor SKU, different photo). Same vendor
   may recur across rows but always with a different product (e.g.
   Cremorne's Premium Local Showcase in row A, Champagne and Luxury
   in row B).

   Cake count cut from 7 (MS12) → 4 (MS13) per Lucky's "too many
   desserts" call. Each row gets 2 cakes, one Le Yeahllow and one
   Chéri, interleaved with non-cake products so the marquee
   demonstrates catalogue breadth instead of "we sell cake from two
   bakeries."

   Two rows scroll opposite directions for visual variety. */
function interleave(): { rowA: CarouselItem[]; rowB: CarouselItem[] } {
  // MS25 — rebalanced now that all 11 of Lucky's Notion-curated
  // vendors are accessible. Row A leans on his curated photos for
  // the marquee's "lead" beat; Row B mixes his curation with
  // website-scraped vendors for the depth shot.
  // Total unique cards: 4 cakes + 16 non-cake products. Strict
  // no-overlap rule maintained between rows.
  const rowA: CarouselItem[] = [
    CAKE_PRODUCTS[0],          // Yuzu Hachimitsu (Le Yeahllow)
    ASPIRATIONAL_PRODUCTS[0],  // Maker & Monger hamper (Lucky)
    ASPIRATIONAL_PRODUCTS[5],  // Larkspur bouquet (Lucky)
    CAKE_PRODUCTS[1],          // Eye Candy (Chéri)
    ROW_A_NON_CAKE[2],         // Pinot & Picasso (Lucky's photo)
    ROW_A_NON_CAKE[3],         // Mornington Peninsula (Lucky's photo)
    ROW_A_NON_CAKE[4],         // Hepburn Bathhouse (web)
    ASPIRATIONAL_PRODUCTS[1],  // Patricia Coffee voucher (Lucky)
    ROW_A_NON_CAKE[0],         // Cremorne Premium Local Showcase (web)
    ROW_A_NON_CAKE[5],         // Otao Cooking Class (web)
  ];
  const rowB: CarouselItem[] = [
    CAKE_PRODUCTS[2],          // Strawberry mille feuille (Le Yeahllow)
    ROW_B_NON_CAKE[0],         // Cremorne Champagne and Luxury (web)
    // MS282: was ASPIRATIONAL_PRODUCTS[6] (Cecilia Fox) — vendor
    // relocated to Auckland NZ, no DB row. Swapped for Victoria
    // Whitelaw's White Delight (real DB florist, real .com.au CDN
    // image). Florist beat preserved.
    ROW_B_NON_CAKE[1],         // Victoria Whitelaw White Delight
    ASPIRATIONAL_PRODUCTS[2],  // ST. ALi voucher (Lucky)
    CAKE_PRODUCTS[3],          // Lemon meringue tart (Chéri)
    ASPIRATIONAL_PRODUCTS[4],  // Inner Studio class (Lucky) — NEW MS25
    ROW_B_NON_CAKE[3],         // Endota Spa (web)
    // MS282: was ASPIRATIONAL_PRODUCTS[3] (Botanique Workshop) —
    // they do events not retail (Lucky's Notion), no DB row.
    // Swapped for Pinot & Picasso Couples (real DB workshop vendor;
    // Pinot already appears in rowA with a different product, so
    // the same-vendor-different-row pattern is intentional).
    ROW_B_NON_CAKE[2],         // Pinot & Picasso Couples Class
    ROW_B_NON_CAKE[4],         // Veuve Clicquot hamper (web)
    ASPIRATIONAL_PRODUCTS[7],  // Proud Mary's coffee (Lucky) — NEW MS25, renamed MS46b
  ];

  // MS282 note: every card the showcase renders now points at a
  // vendor that exists in the DB. The deprecated Cecilia Fox +
  // Botanique Workshop entries in ASPIRATIONAL_PRODUCTS are kept
  // for index stability but are intentionally skipped above.
  // ROW_A_NON_CAKE[1] (Victoria Whitelaw Garden Posy) is still
  // unused — kept available for future rotation if rowB ever needs
  // another florist beat.
  return { rowA, rowB };
}

/* ─── Card components ───────────────────────────────────────────
   Two visual variants share the same outer shell so the marquee
   reads as one consistent rhythm. */

function CardShell({ children }: { children: React.ReactNode }) {
  return (
    <article
      className="lembra-mvs-card flex flex-col overflow-hidden rounded-2xl bg-white"
      style={{
        // MS11: card width is now responsive, 170px on mobile (so
        // two-and-a-bit cards fit on a 375px viewport, matching
        // Lucky's "miniature catalogue" call) and 280px on desktop.
        // Width is set inline via CSS variable + overridden in the
        // <style> block at the section root for the @media query.
        width: 280,
        flexShrink: 0,
        border: "1px solid rgba(29,52,97,0.06)",
        transition: "transform 320ms cubic-bezier(0.16,1,0.3,1)",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-4px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "";
      }}
    >
      {children}
    </article>
  );
}

function CardMeta({
  title,
  subtitle,
  categoryTag,
}: {
  title: string;
  subtitle: string;
  categoryTag: string;
}) {
  return (
    <div style={{ padding: "14px 18px 16px" }}>
      <div
        style={{
          fontFamily: monoFamily,
          fontSize: 9,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          color: "rgba(196,86,74,0.72)",
          marginBottom: 4,
        }}
      >
        {categoryTag}
      </div>
      <div
        style={{
          fontFamily: displayFamily,
          fontStyle: "italic",
          fontWeight: 500,
          fontSize: 18,
          color: "#1D3461",
          lineHeight: 1.18,
          letterSpacing: "-0.01em",
        }}
      >
        {title}
      </div>
      <div
        style={{
          fontFamily: monoFamily,
          fontSize: 10,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: "rgba(45,41,38,0.55)",
          marginTop: 6,
        }}
      >
        {subtitle}
      </div>
    </div>
  );
}

function ProductCard({ item }: { item: Extract<CarouselItem, { kind: "product" }> }) {
  return (
    <CardShell>
      <div
        className="lembra-mvs-card-image"
        style={{
          height: 180,
          background: "#FAF6F0",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* MS27: explicit width/height attrs prevent CLS as images
            stream in. The wrapper still constrains layout via its
            own h-180 / w-280 (or mobile overrides), but giving the
            <img> intrinsic dimensions lets the browser reserve box
            space before bytes arrive. */}
        <img
          src={item.image}
          alt={item.alt}
          width={280}
          height={180}
          loading="lazy"
          decoding="async"
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = "none";
          }}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      </div>
      <CardMeta
        title={item.name}
        subtitle={item.vendor}
        categoryTag={item.categoryTag}
      />
    </CardShell>
  );
}

function CategoryCard({ item }: { item: Extract<CarouselItem, { kind: "category" }> }) {
  const Icon = item.Icon;
  return (
    <Link to={item.href} style={{ textDecoration: "none" }}>
      <CardShell>
        <div
          className="lembra-mvs-card-image"
          style={{
            height: 180,
            background: item.swatch,
            position: "relative",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span
            className="inline-flex h-12 w-12 items-center justify-center rounded-full"
            style={{
              background: "rgba(255,255,255,0.18)",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
              border: "1px solid rgba(255,255,255,0.3)",
            }}
          >
            <Icon size={20} strokeWidth={1.6} className="text-white" />
          </span>
        </div>
        <CardMeta
          title={item.name}
          subtitle={item.descriptor}
          categoryTag={item.categoryTag}
        />
      </CardShell>
    </Link>
  );
}

interface MarqueeRowProps {
  items: CarouselItem[];
  direction: "left" | "right";
  duration: number;
  paused: boolean;
}

function MarqueeRow({ items, direction, duration, paused }: MarqueeRowProps) {
  // Items doubled so the loop seam is invisible.
  const doubled = [...items, ...items];
  return (
    <div style={{ overflow: "hidden", position: "relative" }}>
      <div
        className="lembra-mvs-row"
        style={{
          display: "flex",
          gap: 18,
          width: "max-content",
          animation: `lembra-mvs-${direction} ${duration}s linear infinite`,
          animationPlayState: paused ? "paused" : "running",
        }}
      >
        {doubled.map((item, i) =>
          item.kind === "product" ? (
            <ProductCard key={i} item={item} />
          ) : (
            <CategoryCard key={i} item={item} />
          ),
        )}
      </div>
    </div>
  );
}

export function MultiVendorShowcase() {
  const [paused, setPaused] = useState(false);
  const { rowA, rowB } = interleave();

  return (
    <section
      className="relative overflow-hidden"
      style={{
        // MS11: gradient bridges hero → MVS without a harsh boundary.
        // MS364: top of MVS now starts at #FBF3D0 (mid-butter) to
        // match the trust-line band's exit colour exactly. Pre-MS364
        // the section started at #FFF8E7 (cream) and the trust band
        // ended in #FFFFFF (white) — the cream/white step was the
        // visible seam Lucky flagged. Now: trust band #FFF8E7 →
        // #FBF3D0, MVS top #FBF3D0 → butter peak → cream. One
        // continuous wash, the catalogue gets its butter-from-start
        // entry, and the 120px MVS top-bridge div is no longer
        // needed (removed below).
        background:
          "linear-gradient(180deg, #FBF3D0 0%, #F7E8A4 14%, #FBF3D0 55%, #FAF6F0 100%)",
        padding: "clamp(56px, 6.5vw, 80px) 0 clamp(80px, 10vw, 128px)",
      }}
    >
      {/* MS364: MS11's 120px top-bridge fade div removed. The trust-
          line band (Index.tsx) now ramps cream → mid-butter and
          MVS's own gradient picks up at mid-butter — no separate
          bridge layer needed, and removing it lets the catalogue
          section's butter background read FROM the very first pixel
          rather than being washed out by a cream fade overlay. */}

      <style>{`
        @keyframes lembra-mvs-left  { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        @keyframes lembra-mvs-right { from { transform: translateX(-50%); } to { transform: translateX(0); } }
        @media (prefers-reduced-motion: reduce) {
          .lembra-mvs-row { animation: none !important; }
        }
        /* MS11 mobile sizing, cards shrink so 2+ are visible at
           once on a 375px viewport (Lucky's "miniature catalogue"
           call). Image height shrinks proportionally so the card
           keeps its ~3:2 aspect. */
        @media (max-width: 767px) {
          .lembra-mvs-card { width: 170px !important; }
          .lembra-mvs-card-image { height: 120px !important; }
        }
      `}</style>

      {/* Header, contained */}
      <div
        className="px-6 md:px-8"
        style={{ maxWidth: 1180, margin: "0 auto", position: "relative", zIndex: 1 }}
      >
        <AnimatedReveal animation="fadeInUp" duration={700}>
          <div className="grid items-end gap-8 md:gap-16 md:grid-cols-[1.3fr_1fr] mb-10 md:mb-12">
            <div>
              {/* MS365: "Inside the catalogue" eyebrow removed per
                  Lucky's call. The trust-line connector above the
                  section ("Curated catalogue · Concierge handled ·
                  One invoice") + the H2 itself already establish the
                  section's intent; the eyebrow was extra signposting
                  the rhythm didn't need. */}
              <h2
                style={{
                  fontFamily: displayFamily,
                  fontWeight: 500,
                  fontSize: "clamp(36px, 5.4vw, 60px)",
                  lineHeight: 1.04,
                  letterSpacing: "-0.025em",
                  color: "#1D3461",
                  margin: 0,
                }}
              >
                Cakes, hampers, flowers, vouchers,{" "}
                <span style={{ fontStyle: "italic", color: "#C4564A" }}>
                  whatever fits
                </span>
                .
              </h2>
            </div>
            <p
              className="text-base md:text-[17px]"
              style={{
                lineHeight: 1.6,
                color: "rgba(45,41,38,0.72)",
                margin: 0,
                paddingBottom: 8,
                maxWidth: 380,
                fontFamily: sansFamily,
              }}
            >
              Real products from local makers, picked by Lembra to match the
              moment. Tell our concierge who's celebrating, or browse the
              catalogue yourself.
            </p>
          </div>
        </AnimatedReveal>
      </div>

      {/* Marquee, full-bleed */}
      <div
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        style={{ display: "flex", flexDirection: "column", gap: 18, padding: "8px 0" }}
      >
        <MarqueeRow items={rowA} direction="left" duration={75} paused={paused} />
        <MarqueeRow items={rowB} direction="right" duration={85} paused={paused} />
      </div>

      {/* Footer, Browse CTA */}
      <div
        className="px-6 md:px-8"
        style={{ maxWidth: 1180, margin: "0 auto", paddingTop: 36, position: "relative", zIndex: 1 }}
      >
        <div className="flex flex-wrap items-center justify-end gap-3">
          <Link
            to="/browse"
            className="inline-flex items-center gap-1.5 rounded-full border-2 border-navy/15 bg-white/70 px-6 py-3 text-sm font-medium text-navy transition-all hover:border-coral hover:text-coral focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral"
            style={{ fontFamily: sansFamily }}
          >
            Browse the catalogue
            <ArrowRight size={14} strokeWidth={2.5} />
          </Link>
        </div>
      </div>
    </section>
  );
}
